// @ts-strict-ignore
import { Columns } from "@fullscript/aviary-web";
import type { ReactNode } from "react";

import { AnalyticsEvent } from "@shared/hooks/useAnalyticsLogger/AnalyticsEvent";
import type { SearchApplyEntryPoint } from "@shared/hooks/useAnalyticsLogger/StoreAnalyticsEntryPoint";
import { CloseFilterEntryPoint } from "@shared/hooks/useAnalyticsLogger/StoreAnalyticsEntryPoint";
import { useAnalyticsLogger } from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { useCatalogSearch } from "@shared/hooks/useCatalogSearch/useCatalogSearch";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";

import { DesktopFilterContainer } from "../DesktopFilterContainer/DesktopFilterContainer";
import { FilterButton, FilterButtonStyle } from "../FilterButton/FilterButton";

import { DesktopFilterHeader } from "./DesktopFilterHeader/DesktopFilterHeader";

interface Props {
  children: ReactNode;
  entryPointForLogging?: SearchApplyEntryPoint;
  eventNameForLogging?: AnalyticsEvent;
  isCyberMondayEnabled?: boolean;
  isDrawer?: boolean;
  isEnlargeDrawerEnabled?: boolean;
  filterTilesWithSpacing?: boolean;
  narrowFilters?: boolean;
}

const DesktopFilterColumn = ({
  children,
  isDrawer = false,
  isCyberMondayEnabled = false,
  entryPointForLogging,
  eventNameForLogging,
  isEnlargeDrawerEnabled,
  filterTilesWithSpacing,
  narrowFilters = false,
}: Props) => {
  const { activeSearchType, setActiveSearchType } = useCatalogSearch();
  const { tablet } = useBreakpoints();
  const widerDrawer = isDrawer && isEnlargeDrawerEnabled;
  const { isUnauthenticated, isEmerson } = useSharedGlobalConfig();
  const logAnalyticsEvent = useAnalyticsLogger();

  let searchCloseFilterEvent: AnalyticsEvent;

  if (isEmerson && isUnauthenticated) {
    searchCloseFilterEvent = AnalyticsEvent.EMERSON_UNAUTH_SEARCH_CLOSE_FILTER;
  } else if (isEmerson) {
    searchCloseFilterEvent = AnalyticsEvent.EMERSON_SEARCH_CLOSE_FILTER;
  } else {
    searchCloseFilterEvent = AnalyticsEvent.STORE_SEARCH_CLOSE_FILTER;
  }

  const CloseFilterAndLog = () => {
    setActiveSearchType("");
    if (isDrawer && (!isUnauthenticated || isEmerson)) {
      logAnalyticsEvent({
        eventName: searchCloseFilterEvent,
        payload: {
          entry_point: CloseFilterEntryPoint[`${entryPointForLogging}_Catalog_FilterCloseButton`],
        },
      });
    }
  };

  const renderFilterButton = () => {
    if (!isEmerson) {
      return <FilterButton type={FilterButtonStyle.TEXT_AND_ICON} />;
    }

    return null;
  };

  if (tablet.greaterThan && activeSearchType === "filters") {
    const renderHeaderContent = () => {
      if (widerDrawer) {
        return (
          <>
            <DesktopFilterHeader onClick={() => CloseFilterAndLog()} />
            <DesktopFilterContainer
              isDrawer={isDrawer}
              isCyberMondayEnabled={isCyberMondayEnabled}
              entryPointForLogging={entryPointForLogging}
              eventNameForLogging={eventNameForLogging}
              filterTilesWithSpacing={filterTilesWithSpacing}
            />
          </>
        );
      }

      return (
        <>
          {renderFilterButton()}
          <DesktopFilterContainer
            isDrawer={isDrawer}
            isCyberMondayEnabled={isCyberMondayEnabled}
            entryPointForLogging={entryPointForLogging}
            eventNameForLogging={eventNameForLogging}
            filterTilesWithSpacing={filterTilesWithSpacing}
          />
        </>
      );
    };

    return (
      <Columns>
        <Columns.Column columnWidth={widerDrawer || narrowFilters ? 3 : 4}>
          {renderHeaderContent()}
        </Columns.Column>
        <Columns.Column columnWidth={widerDrawer || narrowFilters ? 9 : 8}>
          {children}
        </Columns.Column>
      </Columns>
    );
  }

  return <>{children}</>;
};

export { DesktopFilterColumn };
